import moment from "moment";

/** Display labels for status/priority/severity chips and filters. */
export const DEFECT_STATUS = {
  NEW: "New",
  IN_PROGRESS: "In Progress",
  CORRECTED: "Corrected",
  NO_CORRECTION_NEEDED: "No Correction Needed",
};

/** API status values — GET/PATCH /equipment/defects/ */
export const DEFECT_API_STATUS = {
  NEW: "new",
  IN_PROGRESS: "in_progress",
  CORRECTED: "corrected",
  NO_CORRECTION_NEEDED: "no_correction_needed",
};

export const DEFECT_PRIORITY = {
  LOW: "Low",
  MEDIUM: "Medium",
  HIGH: "High",
};

export const DEFECT_API_PRIORITY = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
};

export const DEFECT_SEVERITY = {
  CRITICAL: "Critical",
  NON_CRITICAL: "Non Critical",
};

export const DEFECT_API_SEVERITY = {
  CRITICAL: "critical",
  NON_CRITICAL: "non_critical",
};

export const DEFECT_SOURCE = {
  INSPECTION: "Inspection",
  MANUAL: "Manual",
};

/** API source values */
export const DEFECT_API_SOURCE = {
  AUTO_CHECK: "auto_check",
  MANUAL: "manual",
};

export const STATUS_CHIP_VARIANT = {
  [DEFECT_STATUS.NEW]: "info",
  [DEFECT_STATUS.IN_PROGRESS]: "warning",
  [DEFECT_STATUS.CORRECTED]: "success",
  [DEFECT_STATUS.NO_CORRECTION_NEEDED]: "default",
};

export const PRIORITY_CHIP_VARIANT = {
  [DEFECT_PRIORITY.LOW]: "success",
  [DEFECT_PRIORITY.MEDIUM]: "warning",
  [DEFECT_PRIORITY.HIGH]: "error",
};

export const formatDefectDate = (date) => {
  if (!date) return "—";

  const parsedDate = moment(date);
  if (!parsedDate.isValid()) return "—";

  return parsedDate.format("MM/DD/YYYY");
};

export const formatDefectDateTime = (date) => {
  if (!date) return "—";

  const parsedDate = moment(date);
  if (!parsedDate.isValid()) return "—";

  return parsedDate.format("MMM D, YYYY · h:mm A");
};

/** Formats API check_type values like `pre_event` → `Pre-event`. */
export const formatDefectCheckType = (checkType) => {
  if (!checkType) return "—";
  return String(checkType)
    .trim()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part, index) => {
      const lower = part.toLowerCase();
      if (index === 0) {
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      }
      return lower;
    })
    .join("-");
};

/**
 * Builds a single location label from origin location fields.
 * Prefer nested `location`, fall back to top-level origin fields.
 */
export const formatDefectOriginLocation = (origin = {}) => {
  if (!origin || typeof origin !== "object") return "—";

  const loc =
    origin.location && typeof origin.location === "object"
      ? origin.location
      : {};

  const locationDetails =
    loc.location_details || origin.location_details || null;
  const city = loc.city || origin.city || null;
  const state = loc.state || origin.state || loc.state_code || origin.state_code || null;
  const county = loc.county || origin.county || null;

  const parts = [locationDetails, city, state, county].filter(
    (part) => part != null && String(part).trim() !== ""
  );

  return parts.length > 0 ? parts.join(", ") : "—";
};

/**
 * Maps defect origin_detail / origin payload for DefectView.
 */
export const mapDefectOrigin = (origin) => {
  if (!origin || typeof origin !== "object") return null;

  return {
    eventName: origin.program_name || "—",
    checkType: formatDefectCheckType(origin.check_type),
    checkTypeApi: origin.check_type || null,
    label: origin.label || origin.check_form_label || "—",
    location: formatDefectOriginLocation(origin),
    checkNumber: origin.check_number || null,
    programId: origin.program_id ?? null,
  };
};

export const DEFECT_PRIORITY_OPTIONS = [
  { id: DEFECT_API_PRIORITY.LOW, name: DEFECT_PRIORITY.LOW },
  { id: DEFECT_API_PRIORITY.MEDIUM, name: DEFECT_PRIORITY.MEDIUM },
  { id: DEFECT_API_PRIORITY.HIGH, name: DEFECT_PRIORITY.HIGH },
];

export const DEFECT_SEVERITY_OPTIONS = [
  { id: DEFECT_API_SEVERITY.CRITICAL, name: DEFECT_SEVERITY.CRITICAL },
  {
    id: DEFECT_API_SEVERITY.NON_CRITICAL,
    name: DEFECT_SEVERITY.NON_CRITICAL,
  },
];

export const DEFECT_SOURCE_OPTIONS = [
  { id: DEFECT_API_SOURCE.AUTO_CHECK, name: DEFECT_SOURCE.INSPECTION },
  { id: DEFECT_API_SOURCE.MANUAL, name: DEFECT_SOURCE.MANUAL },
];

export const DEFECT_STATUS_FILTER_OPTIONS = [
  { id: DEFECT_API_STATUS.NEW, name: DEFECT_STATUS.NEW },
  {
    id: DEFECT_API_STATUS.IN_PROGRESS,
    name: DEFECT_STATUS.IN_PROGRESS,
  },
  {
    id: DEFECT_API_STATUS.CORRECTED,
    name: DEFECT_STATUS.CORRECTED,
  },
  {
    id: DEFECT_API_STATUS.NO_CORRECTION_NEEDED,
    name: DEFECT_STATUS.NO_CORRECTION_NEEDED,
  },
];

export const formatDefectSourceLabel = (source) => {
  if (!source) return "—";
  if (source === DEFECT_API_SOURCE.AUTO_CHECK) return DEFECT_SOURCE.INSPECTION;
  if (source === DEFECT_API_SOURCE.MANUAL) return DEFECT_SOURCE.MANUAL;
  if (source === DEFECT_SOURCE.INSPECTION || source === DEFECT_SOURCE.MANUAL) {
    return source;
  }
  return String(source);
};

export const getDefectStatusIndicatorValue = (status) => {
  if (!status) return status;

  const normalized = String(status).trim().toLowerCase().replace(/\s+/g, "_");

  const statusMap = {
    new: "new",
    in_progress: "in_progress",
    corrected: "corrected",
    no_correction_needed: "no_correction_needed",
  };

  return statusMap[normalized] || normalized;
};

export const formatDefectStatusLabel = (status) => {
  if (!status) return "—";

  const key = getDefectStatusIndicatorValue(status);
  const labelMap = {
    new: DEFECT_STATUS.NEW,
    in_progress: DEFECT_STATUS.IN_PROGRESS,
    corrected: DEFECT_STATUS.CORRECTED,
    no_correction_needed: DEFECT_STATUS.NO_CORRECTION_NEEDED,
  };

  return labelMap[key] || String(status);
};

/** True only when the field has a real value (not null / undefined / blank). */
export const isDefectFieldAssigned = (value) => {
  if (value == null) return false;
  if (typeof value === "string" && !value.trim()) return false;
  return true;
};

export const getDefectPriorityIndicatorValue = (priority) => {
  if (!isDefectFieldAssigned(priority)) {
    return "priority_unassigned";
  }

  const normalized = String(priority).trim().toLowerCase().replace(/[-\s]+/g, "_");

  const priorityMap = {
    low: "priority_low",
    medium: "priority_medium",
    high: "priority_high",
  };

  return priorityMap[normalized] || priority;
};

export const getDefectSeverityIndicatorValue = (severity) => {
  if (!isDefectFieldAssigned(severity)) {
    return "priority_unassigned";
  }

  const normalized = String(severity)
    .trim()
    .toLowerCase()
    .replace(/[-\s]+/g, "_");

  const severityMap = {
    critical: "severity_critical",
    non_critical: "severity_non_critical",
    noncritical: "severity_non_critical",
  };

  return severityMap[normalized] || severity;
};

export const formatDefectPriorityLabel = (priority) => {
  if (!isDefectFieldAssigned(priority)) return "Not assigned";

  const normalized = String(priority).trim().toLowerCase().replace(/[-\s]+/g, "_");
  const labelMap = {
    low: DEFECT_PRIORITY.LOW,
    medium: DEFECT_PRIORITY.MEDIUM,
    high: DEFECT_PRIORITY.HIGH,
  };

  return labelMap[normalized] || String(priority).trim();
};

export const formatDefectSeverityLabel = (severity) => {
  if (!isDefectFieldAssigned(severity)) return "Not assigned";

  const normalized = String(severity)
    .trim()
    .toLowerCase()
    .replace(/[-\s]+/g, "_");
  const labelMap = {
    critical: DEFECT_SEVERITY.CRITICAL,
    non_critical: DEFECT_SEVERITY.NON_CRITICAL,
    noncritical: DEFECT_SEVERITY.NON_CRITICAL,
  };

  return labelMap[normalized] || String(severity).trim();
};

export const isDefectResolved = (defect = {}) => {
  const normalized = String(defect?.status || "")
    .trim()
    .toLowerCase()
    .replace(/[-\s]+/g, "_");

  return (
    normalized === DEFECT_API_STATUS.CORRECTED ||
    normalized === "corrected" ||
    normalized === DEFECT_API_STATUS.NO_CORRECTION_NEEDED ||
    normalized === "no_correction_needed" ||
    normalized === "nocorrectionneeded"
  );
};

/** Returns missing assignment fields for a defect: "priority" and/or "severity". */
export const getDefectAssignmentGaps = (defect = {}) => {
  const gaps = [];
  if (!isDefectFieldAssigned(defect?.priority)) gaps.push("priority");
  // Severity is Critical / Non Critical — any non-empty value counts as assigned.
  if (!isDefectFieldAssigned(defect?.severity)) gaps.push("severity");
  return gaps;
};

export const isDefectReadyForWorkOrder = (defect = {}) =>
  !isDefectResolved(defect) && getDefectAssignmentGaps(defect).length === 0;

export const formatDefectAssignmentGapMessage = (defects = []) => {
  const list = (Array.isArray(defects) ? defects : [defects]).filter(Boolean);
  if (list.some((defect) => isDefectResolved(defect))) {
    return "Closed defects cannot be attached to a work order.";
  }

  const incomplete = list.filter(
    (defect) => getDefectAssignmentGaps(defect).length > 0
  );
  if (!incomplete.length) return "";

  if (incomplete.length === 1) {
    const gaps = getDefectAssignmentGaps(incomplete[0]);
    if (gaps.length === 2) {
      return "No priority or severity is assigned. Please assign both first.";
    }
    if (gaps[0] === "priority") {
      return "No priority is assigned. Please assign priority first.";
    }
    return "No severity is assigned. Please assign severity first.";
  }

  return "One or more selected defects are missing priority or severity. Please assign both before creating a work order.";
};

const formatPersonLabel = (name, id) => {
  if (name) return name;
  if (id != null && id !== "") return `User ${id}`;
  return "—";
};

const getDefectUnitName = (defect = {}) => {
  const equipmentId = defect.equipment_id;
  return (
    defect.equipment_name ||
    defect.unit_name ||
    defect.unit ||
    (equipmentId != null ? `Unit ${equipmentId}` : "—")
  );
};

/** Collect displayable photo URLs from defect / occurrence payloads. */
export const resolveDefectPhotoUrls = (source = {}) => {
  const urls = [];
  const push = (value) => {
    if (typeof value === "string" && value.trim()) {
      urls.push(value.trim());
    }
  };

  // Preferred: signed URLs / keys returned as photo_keys from the API.
  if (Array.isArray(source.photo_keys)) {
    source.photo_keys.forEach(push);
  }

  if (Array.isArray(source.photos)) {
    source.photos.forEach(push);
  }
  if (Array.isArray(source.photo_urls)) {
    source.photo_urls.forEach(push);
  }

  push(source.photo_url);
  push(source.signed_url);
  push(source.photo);

  // Single photo_key — use when it is already a URL (or other renderable string).
  if (typeof source.photo_key === "string" && source.photo_key.trim()) {
    const key = source.photo_key.trim();
    if (/^https?:\/\//i.test(key) || key.includes("/")) {
      push(key);
    }
  }

  return [...new Set(urls)];
};

export const mapDefectApiOccurrence = (occurrence = {}) => ({
  id: occurrence.id,
  occurrenceIndex: occurrence.occurrence_index,
  dateTime:
    occurrence.occurred_at ||
    occurrence.created_at ||
    occurrence.dateTime ||
    null,
  reportedBy: formatPersonLabel(
    occurrence.created_by_name ||
      occurrence.reported_by_name ||
      occurrence.reportedBy,
    occurrence.created_by_id ?? occurrence.reported_by_id
  ),
  comment: occurrence.comment || "",
  photos: resolveDefectPhotoUrls(occurrence),
  odometer: occurrence.odometer ?? null,
  engineHours: occurrence.engine_hours ?? null,
  unitCheckId: occurrence.unit_check_id ?? null,
  submissionSource: occurrence.submission_source || null,
});

/**
 * Maps a Fleet API defect list/detail item to DefectList DataGrid row shape.
 */
export const mapDefectApiToListRow = (defect = {}) => {
  const equipmentId = defect.equipment_id;

  return {
    id: defect.id,
    defectId: defect.defect_number || defect.defectId || "—",
    equipmentId,
    unit: getDefectUnitName(defect),
    checkItem: defect.item || defect.title || defect.checkItem || "—",
    source: formatDefectSourceLabel(defect.source),
    sourceApi: defect.source,
    status: defect.status,
    priority: defect.priority,
    severity: defect.severity,
    repeatCount:
      defect.repeat_count ??
      defect.occurrence_count ??
      defect.repeatCount ??
      0,
    reportedBy: formatPersonLabel(
      defect.created_by_name ||
        defect.reported_by_name ||
        defect.reportedBy ||
        defect.createdBy,
      defect.created_by_id ?? defect.reported_by_id
    ),
    reportedDate:
      defect.reported_at ||
      defect.created_at ||
      defect.reportedDate ||
      defect.createdDate ||
      null,
    workOrderId: defect.work_order_id ?? defect.workOrder ?? null,
  };
};

/**
 * Maps GET /equipment/defects/{id}/ payload to DefectView / DefectLogsPage shape.
 */
export const mapDefectApiToView = (defect = {}) => {
  if (!defect || typeof defect !== "object") return null;

  const equipmentId = defect.equipment_id;
  const occurrences = Array.isArray(defect.occurrences)
    ? defect.occurrences.map(mapDefectApiOccurrence)
    : [];

  const occurrenceTotal =
    defect.occurrence_count ??
    (occurrences.length > 0 ? occurrences.length : null);
  const repeatCount =
    defect.repeat_count != null
      ? Number(defect.repeat_count)
      : defect.repeatCount != null
        ? Number(defect.repeatCount)
        : occurrenceTotal != null
          ? Math.max(0, Number(occurrenceTotal) - 1)
          : 0;

  const description = defect.description || defect.comment || "";
  const photos = resolveDefectPhotoUrls(defect);

  const isManualSource =
    defect.source === DEFECT_API_SOURCE.MANUAL ||
    defect.source === DEFECT_SOURCE.MANUAL;

  const createdByLabel = formatPersonLabel(
    defect.created_by_name || defect.createdBy,
    defect.created_by_id
  );
  const createdAt =
    defect.created_at || defect.reported_at || defect.createdDate || null;

  const severityAssignedBy =
    defect.severity_assigned_by_name ||
    defect.severityAssignedBy ||
    (defect.severity_assigned_by_id != null
      ? `User ${defect.severity_assigned_by_id}`
      : "") ||
    (isManualSource && defect.severity ? createdByLabel : "");

  const severityAssignedOn =
    defect.severity_assigned_at ||
    defect.severityAssignedOn ||
    (isManualSource && defect.severity ? createdAt : null);

  const priorityAssignedBy =
    defect.priority_assigned_by_name ||
    defect.priorityAssignedBy ||
    (defect.priority_assigned_by_id != null
      ? `User ${defect.priority_assigned_by_id}`
      : "") ||
    (isManualSource && defect.priority ? createdByLabel : "");

  const priorityAssignedOn =
    defect.priority_assigned_at ||
    defect.priorityAssignedOn ||
    (isManualSource && defect.priority ? createdAt : null);

  return {
    id: defect.id,
    defectId: defect.defect_number || defect.defectId || "—",
    equipmentId,
    unit: getDefectUnitName(defect),
    checkItem: defect.item || defect.title || defect.checkItem || "—",
    catalog:
      defect.catalog_label ||
      defect.checklist_item_label ||
      defect.catalogLabel ||
      "—",
    checklistItemKey:
      defect.checklist_item_key ||
      defect.catalog_key ||
      defect.field_key ||
      null,
    source: formatDefectSourceLabel(defect.source),
    sourceApi: defect.source,
    sourceItem: defect.field_key || defect.source_item || defect.sourceItem || "—",
    status: defect.status,
    priority: defect.priority || null,
    severity: defect.severity || null,
    repeatCount,
    createdBy: createdByLabel,
    createdDate: createdAt,
    reportedBy: formatPersonLabel(
      defect.created_by_name ||
        defect.reported_by_name ||
        defect.reportedBy ||
        defect.createdBy,
      defect.created_by_id ?? defect.reported_by_id
    ),
    reportedDate:
      defect.reported_at ||
      defect.created_at ||
      defect.reportedDate ||
      defect.createdDate ||
      null,
    correctedBy: formatPersonLabel(
      defect.corrected_by_name || defect.correctedBy,
      defect.corrected_by_id
    ),
    correctedDate: defect.corrected_at || defect.correctedDate || null,
    workOrderId: defect.work_order_id ?? null,
    work_order_number: defect.work_order_number || null,
    workOrder:
      defect.work_order_number ||
      defect.work_order_id ||
      defect.workOrder ||
      null,
    comment: defect.comment || "",
    photos,
    occurrences,
    severityAssignedBy,
    severityAssignedOn,
    priorityAssignedBy,
    priorityAssignedOn,
    defectLogs: Array.isArray(defect.change_logs)
      ? defect.change_logs.map(mapDefectChangeLog)
      : [],
    work_order_comment:
      defect.work_order_comment ||
      defect.work_order_comments ||
      defect.resolution_note ||
      "",
    resolutionNote:
      defect.work_order_comment ||
      defect.work_order_comments ||
      defect.resolution_note ||
      "",
    origin: mapDefectOrigin(
      defect.origin_detail || defect.origin_details || defect.origin
    ),
  };
};

/** Human-readable labels for change_logs field_name values. */
const CHANGE_LOG_FIELD_LABELS = {
  priority: "Priority",
  severity: "Severity",
  status: "Status",
  assignee_id: "Assignee",
  description: "Description",
  resolution_note: "Resolution Note",
  work_order_comment: "Resolution Note",
  title: "Title",
};

const formatChangeLogFieldLabel = (fieldName) => {
  if (!fieldName) return "Field";
  if (CHANGE_LOG_FIELD_LABELS[fieldName]) {
    return CHANGE_LOG_FIELD_LABELS[fieldName];
  }
  return String(fieldName)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatChangeLogValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "Not Assigned";
  }

  const normalized = String(value).trim().toLowerCase();
  const valueMap = {
    high: DEFECT_PRIORITY.HIGH,
    medium: DEFECT_PRIORITY.MEDIUM,
    low: DEFECT_PRIORITY.LOW,
    critical: DEFECT_SEVERITY.CRITICAL,
    non_critical: DEFECT_SEVERITY.NON_CRITICAL,
    new: DEFECT_STATUS.NEW,
    in_progress: DEFECT_STATUS.IN_PROGRESS,
    corrected: DEFECT_STATUS.CORRECTED,
    no_correction_needed: DEFECT_STATUS.NO_CORRECTION_NEEDED,
  };

  if (valueMap[normalized]) return valueMap[normalized];

  return String(value);
};

const getChangeLogAction = (log = {}) => {
  if (log.action || log.event) return log.action || log.event;

  const fieldLabel = formatChangeLogFieldLabel(log.field_name || log.field);
  const fromEmpty =
    log.from_value === "" ||
    log.from_value == null ||
    log.fromValue === "" ||
    log.fromValue == null;

  return fromEmpty ? fieldLabel : `${fieldLabel} Changed`;
};

/** Maps API change_logs[] entries to DefectLogs timeline item shape. */
export const mapDefectChangeLog = (log = {}) => ({
  id: log.id,
  action: getChangeLogAction(log),
  user:
    log.changed_by_name ||
    log.user ||
    log.user_name ||
    formatPersonLabel(log.created_by_name, log.changed_by_id ?? log.user_id) ||
    "System",
  dateTime: log.changed_at || log.dateTime || log.created_at || log.timestamp || null,
  fromValue: formatChangeLogValue(
    log.from_value ?? log.fromValue ?? log.old_value ?? log.from
  ),
  toValue: formatChangeLogValue(
    log.to_value ?? log.toValue ?? log.new_value ?? log.to
  ),
  details: log.notes || log.details || log.message || log.description || null,
  fieldName: log.field_name || log.field || null,
  entityType: log.entityType || log.entity_type || null,
  entityId: log.entityId || log.entity_id || null,
  entityLabel: log.entityLabel || log.entity_label || null,
});

export const DEFECT_LOG_ENTITY = {
  DEFECT: "defect",
  WORK_ORDER: "work_order",
};

export const getDefectLogNavigationPath = (logItem) => {
  const entityType = logItem?.entityType;
  const entityId = logItem?.entityId;

  if (!entityType || !entityId) {
    return null;
  }

  if (entityType === DEFECT_LOG_ENTITY.DEFECT) {
    return `/defects/view/${entityId}`;
  }

  if (entityType === DEFECT_LOG_ENTITY.WORK_ORDER) {
    return `/work-orders/view/${entityId}`;
  }

  return null;
};
